import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import InfoBanner from './InfoBanner';
import { authAPI, billingAPI, serviceMenuAPI } from '../utils/api';
import SubscriptionRestrictionModal from './SubscriptionRestrictionModal';

const ALLOWED_WITH_INACTIVE_SUBSCRIPTION = ['/ai-history', '/profile', '/billing', '/pricing'];

const INACTIVE_SUBSCRIPTION_STATUSES = new Set(['trial', 'expired', 'past_due', 'cancelled']);

function ProtectedRoute({ children, adminOnly = false, requiredPermission = null, serviceKey = null }) {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [hasAdminAccess, setHasAdminAccess] = useState(false);
  const [adminPermissions, setAdminPermissions] = useState([]);
  const [isServiceAllowed, setIsServiceAllowed] = useState(serviceKey ? null : true);
  const [showSubscriptionRestrictionModal, setShowSubscriptionRestrictionModal] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState('');
  const location = useLocation();

  useEffect(() => {
    const isAllowedPath = ALLOWED_WITH_INACTIVE_SUBSCRIPTION.some((path) =>
      location.pathname.startsWith(path)
    );

    const checkAuth = async () => {
      setIsServiceAllowed(serviceKey ? null : true);
      const token = localStorage.getItem('accessToken');
      
      if (!token) {
        setIsAuthenticated(false);
        setIsServiceAllowed(true);
        return;
      }

      try {
        // Декодируем JWT токен (простая проверка базовой части)
        const payload = JSON.parse(atob(token.split('.')[1]));
        const currentTime = Date.now() / 1000;
        
        if (payload.exp < currentTime) {
          // Токен истек
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          setIsAuthenticated(false);
          setIsServiceAllowed(true);
          return;
        }

        let profile = null;
        if (adminOnly || requiredPermission) {
          try {
            profile = await authAPI.getProfile();
          } catch (profileError) {
            console.error('Ошибка обновления прав доступа:', profileError);
          }
        }

        // Получаем роль из профиля или localStorage для обычных маршрутов.
        const role = profile?.role || localStorage.getItem('userRole') || 'user';
        const isAdminAllowed = Boolean(profile?.has_admin_access)
          || localStorage.getItem('hasAdminAccess') === 'true'
          || role === 'admin';
        let storedPermissions = [];
        if (Array.isArray(profile?.admin_permissions)) {
          storedPermissions = profile.admin_permissions;
        } else {
          try {
            storedPermissions = JSON.parse(localStorage.getItem('adminPermissions') || '[]');
          } catch (_error) {
            storedPermissions = [];
          }
        }
        
        setIsAuthenticated(true);
        setUserRole(role);
        setHasAdminAccess(isAdminAllowed);
        setAdminPermissions(storedPermissions);

        if (serviceKey && role !== 'admin') {
          try {
            const response = await serviceMenuAPI.getSettings();
            const serviceSetting = (response.items || []).find((item) => item.key === serviceKey);

            if (serviceSetting?.is_enabled === false) {
              setIsServiceAllowed(false);
              setShowSubscriptionRestrictionModal(false);
              return;
            }
          } catch (serviceError) {
            console.error('Ошибка проверки доступности раздела:', serviceError);
          }
        }

        setIsServiceAllowed(true);

        if (adminOnly || isAdminAllowed || role === 'admin' || isAllowedPath) {
          setShowSubscriptionRestrictionModal(false);
          return;
        }

        try {
          const subscription = await billingAPI.getSubscription();
          const shouldShowRestriction =
            subscription?.is_active === false &&
            INACTIVE_SUBSCRIPTION_STATUSES.has(subscription?.status);

          setSubscriptionStatus(subscription?.status || '');
          setShowSubscriptionRestrictionModal(Boolean(shouldShowRestriction));
        } catch (subscriptionError) {
          console.error('Ошибка проверки статуса подписки:', subscriptionError);
          setShowSubscriptionRestrictionModal(false);
        }
      } catch (error) {
        console.error('Ошибка при проверке токена:', error);
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        setIsAuthenticated(false);
        setIsServiceAllowed(true);
      }
    };

    checkAuth();
  }, [adminOnly, location.pathname, requiredPermission, serviceKey]);

  // Показываем загрузку пока проверяем аутентификацию
  if (isAuthenticated === null || isServiceAllowed === null) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Если не авторизован - редирект на логин
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Если требуются права админа, но их нет
  if (adminOnly && !hasAdminAccess) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900">Доступ запрещен</h2>
          <p className="mt-2 text-gray-600">У вас недостаточно прав для доступа к этой странице</p>
          <button
            onClick={() => window.history.back()}
            className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
          >
            Назад
          </button>
        </div>
      </div>
    );
  }

  if (requiredPermission && userRole !== 'admin' && !adminPermissions.includes(requiredPermission)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900">Доступ запрещен</h2>
          <p className="mt-2 text-gray-600">У вас недостаточно прав для доступа к этой странице</p>
          <button
            onClick={() => window.history.back()}
            className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
          >
            Назад
          </button>
        </div>
      </div>
    );
  }

  if (!isServiceAllowed) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900">Раздел недоступен</h2>
          <p className="mt-2 text-gray-600">Администратор временно отключил доступ к этому разделу</p>
          <button
            onClick={() => window.location.assign('/dashboard')}
            className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
          >
            На главную
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <InfoBanner />
      {children}
      <SubscriptionRestrictionModal
        isOpen={showSubscriptionRestrictionModal}
        status={subscriptionStatus}
        onClose={() => setShowSubscriptionRestrictionModal(false)}
      />
    </>
  );
}

export default ProtectedRoute; 